import React, { useState } from 'react';
import DashboardLayout from '../components/layouts/DashboardLayout';
import {
  HomepageForm,
  HeroSectionForm,
  QuickAccessForm,
  QuickLinksForm,
  HighlightsForm,
  NewsForm,
  CountdownForm,
  AboutUsHeroForm,
  AboutUsPrincipalForm,
  AboutUsValuesForm,
  AboutUsVisionMissionValuesForm,
  AboutUsPhilosophyForm,
  AboutUsHistoryForm,
  AboutUsLeadershipForm,
  AboutUsForm,
  EducationForm,
  EducationProgramHeroForm,
  EducationProgramLevelsForm,
  EducationCurriculumOverviewForm,
  EducationSubjectsOfferedForm,
  EducationLearningApproachForm,
  EducationAssessmentSystemForm,
  EducationSpecialProgramsForm,
  AdmissionsHeroForm,
  AdmissionsLevelsForm,
  AdmissionsProcessForm,
  AdmissionsRequirementsForm,
  AdmissionsKeyDatesForm,
  AdmissionsFeesPaymentForm,
  AdmissionsOnlineApplicationForm,
  StudentLifeForm,
  StudentLifeHeroForm,
  StudentLifeClubsForm,
  StudentLifeEventsForm,
  StudentLifeHouseSystemForm,
  StudentLifeWellbeingForm,
  StudentLifeCharacterShapingForm,
  FacilitiesForm,
  NewsUpdatesForm,
  ParentsForm,
  CareersForm
} from './website-admin/forms';
import { getSectionConfig, isCMSTab } from '../config/sectionMapping';
import '../styles/dashboard.css';

/**
 * WebsiteAdminDashboard Component
 * Main dashboard for Website Admin role
 * Routes to specific section forms based on selection
 */
function WebsiteAdminDashboard({ user, onLogout, apiBase }) {
  const [activeTab, setActiveTab] = useState('dashboard');

  const sectionConfig = getSectionConfig(activeTab);
  const isShowingSection = isCMSTab(activeTab);

  // Map menu prefixes to form components
  const getFormComponent = () => {
    if (!activeTab.startsWith('cms-')) return null;

    // Hero section - use dedicated HeroSectionForm
    if (activeTab === 'cms-hero') {
      return HeroSectionForm;
    }

    // Quick Access section - use dedicated QuickAccessForm
    if (activeTab === 'cms-quickaccess') {
      return QuickAccessForm;
    }

    // Quick Links section - use dedicated QuickLinksForm
    if (activeTab === 'cms-quicklinks') {
      return QuickLinksForm;
    }

    // Highlights section - use dedicated HighlightsForm
    if (activeTab === 'cms-highlights') {
      return HighlightsForm;
    }

    // News section - use dedicated NewsForm
    if (activeTab === 'cms-news') {
      return NewsForm;
    }
    
    // Countdown section - use dedicated CountdownForm
    if (activeTab === 'cms-countdown') {
      return CountdownForm;
    }
    
    // About Us Hero - use dedicated AboutUsHeroForm
    if (activeTab === 'cms-about-hero') {
      return AboutUsHeroForm;
    }
    
    // About Us Principal - use dedicated AboutUsPrincipalForm
    if (activeTab === 'cms-about-principal') {
      return AboutUsPrincipalForm;
    }
    
    // About Us Vision Mission Values - use dedicated AboutUsVisionMissionValuesForm (simplified form)
    if (activeTab === 'cms-about-values') {
      return AboutUsVisionMissionValuesForm;
    }
    
    // About Us Philosophy - use dedicated AboutUsPhilosophyForm
    if (activeTab === 'cms-about-philosophy') {
      return AboutUsPhilosophyForm;
    }
    
    // About Us History - use dedicated AboutUsHistoryForm (includes History, Why Started, Established)
    if (activeTab === 'cms-about-history') {
      return AboutUsHistoryForm;
    }
    
    // About Us Leadership - use dedicated AboutUsLeadershipForm
    if (activeTab === 'cms-about-leadership') {
      return AboutUsLeadershipForm;
    }
    
    // About Us other tabs: cms-about-* (uses legacy AboutUsForm)
    if (activeTab.startsWith('cms-about-')) return AboutUsForm;
    
    // Education Program Hero - use dedicated EducationProgramHeroForm
    if (activeTab === 'cms-education-hero') {
      return EducationProgramHeroForm;
    }
    
    // Education Program Levels - use dedicated EducationProgramLevelsForm
    if (activeTab === 'cms-edu-programs') {
      return EducationProgramLevelsForm;
    }
    
    // Education Curriculum Overview - use dedicated EducationCurriculumOverviewForm
    if (activeTab === 'cms-edu-curriculum') {
      return EducationCurriculumOverviewForm;
    }
    
    // Education Subjects Offered - use dedicated EducationSubjectsOfferedForm
    if (activeTab === 'cms-edu-subjects') {
      return EducationSubjectsOfferedForm;
    }
    
    // Education Learning Approach - use dedicated EducationLearningApproachForm
    if (activeTab === 'cms-edu-learning') {
      return EducationLearningApproachForm;
    }
    
    // Education Assessment System - use dedicated EducationAssessmentSystemForm
    if (activeTab === 'cms-edu-assessment') {
      return EducationAssessmentSystemForm;
    }
    
    // Education Special Programs - use dedicated EducationSpecialProgramsForm
    if (activeTab === 'cms-edu-special-programs') {
      return EducationSpecialProgramsForm;
    }
    
    // Admissions Hero - use dedicated AdmissionsHeroForm
    if (activeTab === 'cms-adm-hero') {
      return AdmissionsHeroForm;
    }
    
    // Admissions Levels - use dedicated AdmissionsLevelsForm
    if (activeTab === 'cms-adm-levels') {
      return AdmissionsLevelsForm;
    }
    
    // Admissions Process - use dedicated AdmissionsProcessForm
    if (activeTab === 'cms-adm-process') {
      return AdmissionsProcessForm;
    }
    
    // Admissions Requirements - use dedicated AdmissionsRequirementsForm
    if (activeTab === 'cms-adm-requirements') {
      return AdmissionsRequirementsForm;
    }
    
    // Admissions Key Dates - use dedicated AdmissionsKeyDatesForm
    if (activeTab === 'cms-adm-timeline') {
      return AdmissionsKeyDatesForm;
    }
    
    // Admissions Fees & Payment - use dedicated AdmissionsFeesPaymentForm
    if (activeTab === 'cms-adm-fees') {
      return AdmissionsFeesPaymentForm;
    }
    
    // Admissions Online Application - use dedicated AdmissionsOnlineApplicationForm
    if (activeTab === 'cms-adm-online-app') {
      return AdmissionsOnlineApplicationForm;
    }
    
    // Student Life Hero - use dedicated StudentLifeHeroForm
    if (activeTab === 'cms-life-hero') {
      return StudentLifeHeroForm;
    }
    
    // Student Life Clubs - use dedicated StudentLifeClubsForm
    if (activeTab === 'cms-life-clubs') {
      return StudentLifeClubsForm;
    }
    
    // Student Life Events - use dedicated StudentLifeEventsForm
    if (activeTab === 'cms-life-events') {
      return StudentLifeEventsForm;
    }
    
    // Student Life House System - use dedicated StudentLifeHouseSystemForm
    if (activeTab === 'cms-life-house-system') {
      return StudentLifeHouseSystemForm;
    }
    
    // Student Life Wellbeing - use dedicated StudentLifeWellbeingForm
    if (activeTab === 'cms-life-wellbeing') {
      return StudentLifeWellbeingForm;
    }
    
    // Student Life Character Shaping - use dedicated StudentLifeCharacterShapingForm
    if (activeTab === 'cms-life-character-shaping') {
      return StudentLifeCharacterShapingForm;
    }
    
    // Student Life other tabs (fallback)
    if (activeTab.startsWith('cms-life-')) return StudentLifeForm;
    
    // Facilities tabs: cms-fac-*
    if (activeTab.startsWith('cms-fac-')) return FacilitiesForm;
    
    // News & Updates tabs: cms-news-* (but not cms-news for homepage, which we handle above)
    if (activeTab.startsWith('cms-news-')) return NewsUpdatesForm;
    
    // Parents tabs: cms-par-*
    if (activeTab.startsWith('cms-par-')) return ParentsForm;
    
    // Careers tabs: cms-car-*
    if (activeTab.startsWith('cms-car-')) return CareersForm;
    
    return null;
  };

  const FormComponent = getFormComponent();

  return (
    <DashboardLayout user={user} onLogout={onLogout} onTabChange={setActiveTab} userRole="website_admin">
      <div className="dashboard-content">
        {activeTab === 'dashboard' && (
          <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>
            <p style={{fontSize: '18px', fontWeight: '600'}}>Website Admin Dashboard</p>
            <p>Pilih section di sidebar untuk mengedit konten</p>
          </div>
        )}

        {isShowingSection && FormComponent && <FormComponent apiBase={apiBase} activeTab={activeTab} />}
      </div>
    </DashboardLayout>
  );
}

export default WebsiteAdminDashboard;
